import styled, { keyframes } from "styled-components";

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const pulseRing = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.6;
  }
`;

export const HeroContainer = styled.div`
  background: #08070d;
  display: flex;
  justify-content: center;
  position: relative;
  padding: 70px 24px 90px 24px;
  z-index: 1;
  overflow: hidden;

  @media (max-width: 960px) {
    padding: 40px 16px 70px 16px;
  }
  @media (max-width: 640px) {
    padding: 30px 16px 60px 16px;
  }
`;

export const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

export const HeroInnerContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1150px;
  gap: 40px;
  z-index: 2;

  @media (max-width: 960px) {
    flex-direction: column-reverse;
    gap: 36px;
  }
`;

export const HeroLeftContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  z-index: 10;

  @media (max-width: 960px) {
    align-items: center;
    text-align: center;
  }
`;

export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.35);
  color: #4ade80;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 6px 14px;
  border-radius: 9999px;
  width: fit-content;

  span.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 10px #22c55e;
  }
`;

export const Title = styled.h1`
  font-weight: 800;
  font-size: clamp(2.6rem, 5.8vw, 4.2rem);
  color: ${({ theme }) => theme.text_primary || "#ffffff"};
  line-height: 1.12;
  letter-spacing: -0.03em;
  margin: 0;

  .name-gradient {
    background: linear-gradient(135deg, #ffffff 0%, #c084fc 45%, #854CE6 80%, #38bdf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: block;
    filter: drop-shadow(0 0 24px rgba(133, 76, 230, 0.5));
  }
`;

export const TextLoop = styled.div`
  font-weight: 700;
  font-size: clamp(1.25rem, 3.2vw, 1.85rem);
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.4;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

export const Span = styled.span`
  color: #854CE6;
  text-shadow: 0 0 18px rgba(133, 76, 230, 0.6);
`;

/* Core Tech Stack Pill Ticker: React • Node • MongoDB • Next.js */
export const TechStackBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(133, 76, 230, 0.35);
  border-radius: 12px;
  padding: 8px 16px;
  font-size: 13.5px;
  font-weight: 600;
  color: #ffffff;
  width: fit-content;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(10px);
  margin-top: 2px;
  margin-bottom: 4px;

  span.tech-dot {
    color: #854CE6;
    font-size: 14px;
  }

  span.tech-highlight {
    color: #c084fc;
    font-weight: 700;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    padding: 6px 12px;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

export const SubTitle = styled.p`
  font-size: 15.5px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.72);
  margin: 0;
  max-width: 540px;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

export const SocialMediaIcons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin: 4px 0 10px 0;
`;

export const SocialMediaIcon = styled.a`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: rgba(133, 76, 230, 0.2);
    border-color: rgba(133, 76, 230, 0.6);
    color: #ffffff;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(133, 76, 230, 0.35);
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

export const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #854CE6 0%, #a855f7 100%);
  color: #ffffff;
  padding: 13px 26px;
  border-radius: 14px;
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(133, 76, 230, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(133, 76, 230, 0.6);
    filter: brightness(1.1);
  }
`;

export const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #ffffff;
  padding: 13px 22px;
  border-radius: 14px;
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(133, 76, 230, 0.5);
    color: #e879f9;
    transform: translateY(-2px);
  }
`;

export const HeroRightContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

export const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 360px;
  height: 360px;
  z-index: 2;

  @media (max-width: 640px) {
    width: 270px;
    height: 270px;
  }
`;

export const GlowingOrb = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(133, 76, 230, 0.45) 0%,
    rgba(190, 26, 219, 0.2) 45%,
    rgba(56, 189, 248, 0.1) 70%,
    transparent 80%
  );
  filter: blur(40px);
  animation: ${pulseRing} 4s ease-in-out infinite;
  z-index: 1;
`;

export const Img = styled.img`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(133, 76, 230, 0.65);
  box-shadow: 0 0 35px rgba(133, 76, 230, 0.45);
  z-index: 3;
  transition: all 0.4s ease;

  &:hover {
    border-color: #c084fc;
    box-shadow: 0 0 50px rgba(192, 132, 252, 0.7);
    transform: scale(1.02);
  }
`;

export const FloatingBadgeLeft = styled.div`
  position: absolute;
  top: 10px;
  left: -25px;
  background: rgba(12, 11, 20, 0.94);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(133, 76, 230, 0.5);
  border-radius: 12px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 700;
  color: #ffffff;
  z-index: 4;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  animation: ${float} 3.5s ease-in-out infinite;

  @media (max-width: 640px) {
    top: 0px;
    left: -10px;
    font-size: 11px;
    padding: 6px 10px;
  }
`;

export const FloatingBadgeRight = styled.div`
  position: absolute;
  bottom: 15px;
  right: -25px;
  background: rgba(12, 11, 20, 0.94);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(56, 189, 248, 0.5);
  border-radius: 12px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 700;
  color: #ffffff;
  z-index: 4;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  animation: ${float} 4s ease-in-out infinite 0.5s;

  @media (max-width: 640px) {
    bottom: 0px;
    right: -10px;
    font-size: 11px;
    padding: 6px 10px;
  }
`;
